import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { StorefrontHeader } from "../components/StorefrontHeader";
import { WhatsAppFloat } from "../components/WhatsAppFloat";
import { Button } from "../components/ui/button";
import { QRCodeCanvas } from "qrcode.react";
import {
  MessageCircle,
  Phone,
  MapPin,
  Clock,
  CalendarOff,
  Share2,
  Copy,
  QrCode,
  Printer,
  Download,
  ShoppingBasket,
  UtensilsCrossed,
} from "lucide-react";

const waLink = (phone) => `https://wa.me/${String(phone || "").replace(/[^\d]/g, "")}`;

export default function InfoToko() {
  const { t, lang } = useI18n();
  const days =
    {
      id: ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"],
      ja: ["日曜", "月曜", "火曜", "水曜", "木曜", "金曜", "土曜"],
      en: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    }[lang] || [];
  const [s, setS] = useState(null);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setS(data));
  }, []);

  if (!s) return <div className="p-8 text-muted-foreground">…</div>;

  const qrCanvas = () => document.querySelector("#qr-card canvas");

  const downloadQr = () => {
    const canvas = qrCanvas();
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `qr-${(s.store_name || "toko").replace(/\s+/g, "-").toLowerCase()}.png`;
    a.click();
  };

  const printQr = () => {
    const canvas = qrCanvas();
    if (!canvas) return;
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${s.store_name}</title>
      <style>
        @page { size: A4; margin: 15mm; }
        body { margin: 0; font-family: Arial, "Hiragino Sans", sans-serif; }
        .card { width: 80mm; border: 1px dashed #999; border-radius: 4mm; padding: 8mm 6mm;
          text-align: center; margin: 0 auto; }
        img.logo { width: 18mm; height: 18mm; border-radius: 4mm; object-fit: cover; }
        img.qr { width: 52mm; height: 52mm; margin: 4mm 0; }
        h1 { font-size: 15px; margin: 2mm 0 1mm; }
        p { font-size: 10px; color: #444; margin: 0; }
      </style></head><body><div class="card">
      <img class="logo" src="${window.location.origin}/logo.png" alt="" />
      <img class="qr" src="${canvas.toDataURL("image/png")}" alt="QR" />
      <h1>${s.store_name}</h1><p>${s.address}</p><p>${s.phone}</p>
      </div><script>window.onload = function(){ setTimeout(window.print, 300); }</script>
      </body></html>`;
    const w = window.open("", "_blank", "width=800,height=900");
    if (!w) return;
    const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    w.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const share = async () => {
    const url = window.location.href;
    const payload = { title: s.store_name, text: `${s.store_name} · ${s.address}`, url };
    try {
      if (navigator.share) await navigator.share(payload);
      else {
        await navigator.clipboard.writeText(url);
        toast.success(t("link_copied"));
      }
    } catch (err) {
      console.error("Share dibatalkan atau gagal:", err);
    }
  };

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <StorefrontHeader active="info" />
      <div className="w-full max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-8 space-y-6">
        <div
          className="rounded-3xl bg-gray-900 text-white p-6 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center gap-6"
          data-testid="info-card"
        >
          <img
            src="/logo.png"
            alt={s.store_name}
            className="h-24 w-24 rounded-2xl object-cover shrink-0"
            data-testid="info-logo"
          />
          <div className="min-w-0 space-y-2">
            <h1 className="font-display text-3xl sm:text-4xl font-semibold leading-tight">
              {s.store_name}
            </h1>
            <p className="text-sm text-white/75 flex items-start gap-2">
              <MapPin size={15} className="mt-0.5 shrink-0" /> {s.address}
            </p>
            <p className="text-sm text-white/75 flex items-center gap-2">
              <Phone size={15} /> {s.phone}
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <a href={waLink(s.phone)} target="_blank" rel="noreferrer" data-testid="info-wa-btn">
            <Button className="w-full h-12 bg-emerald-600 hover:bg-emerald-700">
              <MessageCircle size={17} className="mr-2" /> {t("chat_whatsapp")}
            </Button>
          </a>
          <Button
            variant="outline"
            className="h-12"
            data-testid="info-share-btn"
            onClick={share}
          >
            {navigator.share ? (
              <Share2 size={16} className="mr-2" />
            ) : (
              <Copy size={16} className="mr-2" />
            )}
            {t("share_store")}
          </Button>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3" data-testid="info-hours">
          <p className="font-display font-semibold flex items-center gap-2">
            <Clock size={18} className="text-primary" /> {t("business_hours")}
          </p>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1">
            {(s.hours || []).map((h) => (
              <div key={h.day} className="flex justify-between text-sm border-b border-gray-100 py-1">
                <span>{days[h.day]}</span>
                <span className={h.closed ? "text-destructive" : "num"}>
                  {h.closed ? t("closed") : `${h.open}–${h.close}`}
                </span>
              </div>
            ))}
          </div>
          {s.holiday_notice && (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 flex items-center gap-2">
              <CalendarOff size={15} /> {s.holiday_notice}
            </p>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden" data-testid="info-map">
          <iframe
            title="map"
            className="w-full h-72 border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            src={`https://www.google.com/maps?q=${encodeURIComponent(s.address)}&output=embed`}
          />
          <a
            className="block px-5 py-3 text-sm text-primary hover:underline"
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.address)}`}
            target="_blank"
            rel="noreferrer"
            data-testid="info-map-link"
          >
            {t("open_in_maps")}
          </a>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3" data-testid="info-qr">
          <p className="font-display font-semibold flex items-center gap-2">
            <QrCode size={18} className="text-primary" /> {t("qr_title")}
          </p>
          <p className="text-xs text-muted-foreground">{t("qr_hint")}</p>
          <div id="qr-card" className="flex flex-col items-center gap-3 rounded-2xl border border-gray-200 p-6 w-full max-w-[280px] mx-auto">
            <img src="/logo.png" alt={s.store_name} className="h-14 w-14 rounded-xl object-cover" />
            <QRCodeCanvas
              value={window.location.href}
              size={188}
              level="M"
              includeMargin
              data-testid="qr-canvas"
            />
            <p className="font-display font-semibold text-center leading-tight">{s.store_name}</p>
            <p className="text-xs text-muted-foreground text-center max-w-[200px]">{s.address}</p>
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            <Button variant="outline" data-testid="qr-print-btn" onClick={printQr}>
              <Printer size={16} className="mr-2" /> {t("qr_print")}
            </Button>
            <Button variant="outline" data-testid="qr-download-btn" onClick={downloadQr}>
              <Download size={16} className="mr-2" /> {t("qr_download")}
            </Button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Link to="/toko" data-testid="info-goto-toko">
            <Button variant="outline" className="w-full h-12">
              <ShoppingBasket size={16} className="mr-2" /> {t("go_store")}
            </Button>
          </Link>
          <Link to="/resto" data-testid="info-goto-resto">
            <Button variant="outline" className="w-full h-12">
              <UtensilsCrossed size={16} className="mr-2" /> {t("go_resto")}
            </Button>
          </Link>
        </div>
      </div>
      <WhatsAppFloat />
    </div>
  );
}
